import QueryStatus from '../../ui/QueryStatus';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import * as provider from '../../data/community/communityDataProvider';
import { useCommunityDataSource } from '../../utils/communityDataSource';

const COLORS = ['#b8c5ed', '#4a90d9', '#22c55e', '#ef4444'];

export default function CommunityOverview() {
  const dataSource = useCommunityDataSource();
  const statsQuery = useQuery({
    queryKey: ['communityOverview', dataSource],
    queryFn: () => provider.getOverviewStats(),
  });

  const s = statsQuery.data || {};
  if (statsQuery.isError && !statsQuery.data) return <QueryStatus query={statsQuery} source={dataSource === 'mock' ? 'Mock' : 'API'} />;
  const pieData = [
    { name: 'Regional', value: s.regional_count || 0 },
    { name: 'General', value: s.general_count || 0 },
  ];

  const votePie = [
    { name: 'Upvotes', value: s.total_up || 0 },
    { name: 'Downvotes', value: s.total_down || 0 },
  ];

  const barData = [
    { name: 'Threads', count: s.total_threads || 0 },
    { name: 'Comments', count: s.comment_count || 0 },
    { name: 'Boards', count: s.board_count || 0 },
    { name: 'GPS threads', count: s.gps_count || 0 },
    ...(s.open_reports == null ? [] : [{ name: 'Open reports', count: s.open_reports }]),
  ];

  const statCards = [
    ['Total threads', s.total_threads],
    ['Regional', s.regional_count],
    ['Total views', s.total_views],
    ['Upvotes', s.total_up],
    ['Downvotes', s.total_down],
    ['Boards', s.board_count],
    ['Tagged threads', s.tagged_thread_count],
    ['On boards', s.board_thread_count],
  ];

  return (
    <div className="space-y-6">
      <QueryStatus query={statsQuery} source={dataSource === 'mock' ? 'Mock' : 'API'} />
      {statsQuery.error && (
        <p className="text-red-400 text-sm">{statsQuery.error.message}</p>
      )}
      {s.partial && (
        <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 text-sky-200 text-xs px-4 py-3">
          Aggregated live from the latest 100 threads of the community API. Reports count is not
          exposed by the API.
        </div>
      )}
      {!s.partial && dataSource === 'mock' && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs px-4 py-3">
          Mock dashboard — includes v2 metrics: votes (up/down), boards, tags, GPS threads, and
          trending exclusion (threads with ▼10+ are hidden from trending feed).
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map(([label, val]) => (
          <div key={label} className="bg-dark-card border border-dark-border rounded-lg p-4">
            <p className="text-xs text-dark-text-muted">{label}</p>
            <p className="text-2xl font-bold text-dark-text mt-1">{val ?? '—'}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-dark-card border border-dark-border rounded-lg p-4 h-64">
          <p className="text-sm text-dark-text-secondary mb-2">Thread kinds</p>
          <ResponsiveContainer width="100%" height="90%">
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {pieData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-dark-card border border-dark-border rounded-lg p-4 h-64">
          <p className="text-sm text-dark-text-secondary mb-2">Votes (v2)</p>
          <ResponsiveContainer width="100%" height="90%">
            <PieChart>
              <Pie data={votePie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {votePie.map((_, i) => (
                  <Cell key={i} fill={i === 0 ? '#22c55e' : '#ef4444'} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-dark-card border border-dark-border rounded-lg p-4 h-64">
          <p className="text-sm text-dark-text-secondary mb-2">Activity & boards</p>
          <ResponsiveContainer width="100%" height="90%">
            <BarChart data={barData}>
              <XAxis dataKey="name" tick={{ fill: '#aaa', fontSize: 10 }} />
              <YAxis tick={{ fill: '#aaa', fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#b8c5ed" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
